import { useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuid } from "uuid";

import { View, Text, Button, DropdownField, InputField, Icon } from "../base";

type JsonSchemaPropertyType = "string" | "number" | "boolean" | "array";
type JsonSchemaArrayItemType = "string" | "number" | "boolean";

type SchemaProperty = {
  name: string;
  type: JsonSchemaPropertyType;
  itemType?: JsonSchemaArrayItemType;
};

type ConditionOperator = "$gt" | "$gte" | "$lt" | "$lte" | "$eq" | "$ne" | "$in" | "$nin" | "$inc" | "$ninc";

const OPERATOR_LABELS: Record<ConditionOperator, string> = {
  $eq: "Equals",
  $ne: "Not Equal",
  $gt: "Greater Than",
  $gte: "Greater Than or Equal",
  $lt: "Less Than",
  $lte: "Less Than or Equal",
  $in: "In List",
  $nin: "Not In List",
  $inc: "Includes",
  $ninc: "Does Not Include",
};

type CriteriaEntry = {
  id: string;
  propertyName: string;
  operator: ConditionOperator;
  value: string;
};

type SuccessCriteriaBuilderProps = {
  schema?: Record<string, unknown> | null;
  value?: Record<string, unknown> | null;
  onChange: (criteria: Record<string, unknown> | null) => void;
  className?: string;
};

const getSchemaProperties = (schema?: Record<string, unknown> | null): SchemaProperty[] => {
  if (!schema || typeof schema !== "object" || !schema.properties) {
    return [];
  }

  const properties = schema.properties as Record<string, { type?: string; items?: { type?: string } }>;

  return Object.entries(properties)
    .map(([name, definition]) => ({
      name,
      type: (definition?.type || "string") as JsonSchemaPropertyType,
      itemType: definition?.type === "array" ? (definition.items?.type as JsonSchemaArrayItemType) || "string" : undefined,
    }))
    .filter((property) => ["string", "number", "boolean", "array"].includes(property.type));
};

const getAllowedOperators = (type: JsonSchemaPropertyType): ConditionOperator[] => {
  switch (type) {
    case "number":
      return ["$eq", "$gt", "$gte", "$lt", "$lte", "$ne", "$in", "$nin"];
    case "boolean":
      return ["$eq"];
    case "array":
      return ["$eq", "$inc", "$ninc"];
    case "string":
    default:
      return ["$eq", "$ne", "$in", "$nin"];
  }
};

const parseCriteria = (criteria?: Record<string, unknown> | null): CriteriaEntry[] => {
  if (!criteria || typeof criteria !== "object") {
    return [];
  }

  const entries: CriteriaEntry[] = [];

  Object.entries(criteria).forEach(([propertyName, rawValue]) => {
    if (rawValue && typeof rawValue === "object" && !Array.isArray(rawValue)) {
      const rawObject = rawValue as Record<string, unknown>;
      const operators = Object.keys(rawObject) as ConditionOperator[];

      operators.forEach((operatorKey) => {
        const operator = operatorKey as ConditionOperator;
        const conditionValue = rawObject[operator];

        entries.push({
          id: uuid(),
          propertyName,
          operator,
          value: Array.isArray(conditionValue) ? conditionValue.join(", ") : String(conditionValue ?? ""),
        });
      });

      if (operators.length === 0) {
        entries.push({
          id: uuid(),
          propertyName,
          operator: "$eq",
          value: "",
        });
      }

      return;
    }

    entries.push({
      id: uuid(),
      propertyName,
      operator: "$eq",
      value: String(rawValue ?? ""),
    });
  });

  return entries;
};

const parseInputValue = (value: string, type: JsonSchemaPropertyType, operator: ConditionOperator, itemType?: JsonSchemaArrayItemType): unknown => {
  const trimmedValue = value.trim();

  const splitList = () =>
    trimmedValue
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);

  if (operator === "$in" || operator === "$nin") {
    const values = splitList();

    if (type === "number") {
      return values.map((item) => Number(item)).filter((item) => !Number.isNaN(item));
    }

    if (type === "boolean") {
      return values.map((item) => item.toLowerCase()).map((item) => item === "true");
    }

    if (type === "array") {
      if (itemType === "number") {
        return values.map((item) => Number(item)).filter((item) => !Number.isNaN(item));
      }

      if (itemType === "boolean") {
        return values.map((item) => item.toLowerCase()).map((item) => item === "true");
      }

      return values;
    }

    return values;
  }

  if (operator === "$inc" || operator === "$ninc") {
    if (type === "array") {
      if (itemType === "number") {
        const parsed = Number(trimmedValue);
        return Number.isNaN(parsed) ? 0 : parsed;
      }
      if (itemType === "boolean") {
        return trimmedValue.toLowerCase() === "true";
      }
      return trimmedValue;
    }

    return trimmedValue;
  }

  if (type === "array") {
    const values = splitList();

    if (itemType === "number") {
      return values.map((item) => Number(item)).filter((item) => !Number.isNaN(item));
    }

    if (itemType === "boolean") {
      return values.map((item) => item.toLowerCase()).map((item) => item === "true");
    }

    return values;
  }

  if (type === "number") {
    const parsed = Number(trimmedValue);

    return Number.isNaN(parsed) ? 0 : parsed;
  }

  if (type === "boolean") {
    return trimmedValue.toLowerCase() === "true";
  }

  return trimmedValue;
};

export const SuccessCriteriaBuilder = ({ schema, value, onChange, className }: SuccessCriteriaBuilderProps) => {
  const properties = useMemo(() => getSchemaProperties(schema), [schema]);
  const [entries, setEntries] = useState<CriteriaEntry[]>(() => parseCriteria(value));
  const lastEmittedRef = useRef<string | null>(null);

  useEffect(() => {
    const incomingString = value ? JSON.stringify(value) : null;

    if (incomingString === lastEmittedRef.current) {
      return;
    }

    setEntries((prevEntries) => {
      const parsed = parseCriteria(value);

      return parsed.map((entry) => {
        const existing = prevEntries.find((prev) => prev.propertyName === entry.propertyName && prev.operator === entry.operator);

        return existing ? { ...entry, id: existing.id } : entry;
      });
    });
  }, [value]);

  useEffect(() => {
    if (properties.length === 0) {
      setEntries([]);
      onChange(null);
      lastEmittedRef.current = null;
      return;
    }

    setEntries((prevEntries) => prevEntries.filter((entry) => properties.some((property) => property.name === entry.propertyName)));
  }, [properties, onChange]);

  const emitCriteria = (currentEntries: CriteriaEntry[]) => {
    const usableEntries = currentEntries.filter((entry) => entry.propertyName && entry.value.trim() !== "");

    if (usableEntries.length === 0) {
      onChange(null);
      lastEmittedRef.current = null;
      return;
    }

    const criteria = usableEntries.reduce<Record<string, unknown>>((acc, entry) => {
      const property = properties.find((item) => item.name === entry.propertyName);

      if (!property) {
        return acc;
      }

      const parsedValue = parseInputValue(entry.value, property.type, entry.operator, property.itemType);

      const existingValue = acc[entry.propertyName];

      if (entry.operator === "$eq") {
        if (existingValue && typeof existingValue === "object" && !Array.isArray(existingValue)) {
          (existingValue as Record<string, unknown>)["$eq"] = parsedValue;
          acc[entry.propertyName] = existingValue;
        } else if (existingValue !== undefined) {
          acc[entry.propertyName] = {
            $eq: parsedValue,
          };
        } else {
          acc[entry.propertyName] = parsedValue;
        }
      } else {
        let container: Record<string, unknown>;

        if (existingValue === undefined) {
          container = {};
        } else if (typeof existingValue === "object" && !Array.isArray(existingValue)) {
          container = { ...(existingValue as Record<string, unknown>) };
        } else {
          container = { $eq: existingValue };
        }

        container[entry.operator] = parsedValue;
        acc[entry.propertyName] = container;
      }

      return acc;
    }, {});

    const criteriaString = JSON.stringify(criteria);
    lastEmittedRef.current = criteriaString;
    onChange(criteria);
  };

  const handleEntryChange = (entryId: string, key: keyof CriteriaEntry, value: string) => {
    setEntries((prevEntries) => {
      const updatedEntries = prevEntries.map((entry) => {
        if (entry.id !== entryId) {
          return entry;
        }

        const nextEntry = {
          ...entry,
          [key]: value,
        } as CriteriaEntry;

        if (key === "propertyName") {
          const property = properties.find((item) => item.name === value);
          if (property) {
            const allowed = getAllowedOperators(property.type);
            if (!allowed.includes(nextEntry.operator)) {
              nextEntry.operator = allowed[0];
            }
          }
        }

        return nextEntry;
      });

      emitCriteria(updatedEntries);

      return updatedEntries;
    });
  };

  const handleAddEntry = () => {
    if (!properties.length) {
      return;
    }

    const defaultProperty = properties[0];
    const defaultOperator = getAllowedOperators(defaultProperty.type)[0];

    setEntries((prevEntries) => {
      const updatedEntries = [
        ...prevEntries,
        {
          id: uuid(),
          propertyName: defaultProperty.name,
          operator: defaultOperator,
          value: "",
        },
      ];

      return updatedEntries;
    });
  };

  const handleRemoveEntry = (entryId: string) => {
    setEntries((prevEntries) => {
      const updatedEntries = prevEntries.filter((entry) => entry.id !== entryId);

      emitCriteria(updatedEntries);

      return updatedEntries;
    });
  };

  if (!properties.length) {
    return (
      <View className={className}>
        <View className="border border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-6 text-center">
          <Text className="text-sm text-gray-500 dark:text-gray-400">Define a response schema to enable success criteria.</Text>
        </View>
      </View>
    );
  }

  return (
    <View className={className}>
      <View className="flex flex-col gap-3">
        {entries.map((entry) => {
          const property = properties.find((item) => item.name === entry.propertyName);
          const operators = property ? getAllowedOperators(property.type) : [];

          return (
            <View
              key={entry.id}
              className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-900 space-y-3"
            >
              <View className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
                <View className="flex-1">
                  <DropdownField
                    label="Property"
                    options={properties.map((item) => ({ value: item.name, label: item.name }))}
                    value={entry.propertyName}
                    onChange={(value) => handleEntryChange(entry.id, "propertyName", value)}
                  />
                </View>
                <View className="w-full md:w-48">
                  <DropdownField
                    label="Condition"
                    options={operators.map((operator) => ({ value: operator, label: OPERATOR_LABELS[operator] }))}
                    value={entry.operator}
                    onChange={(value) => handleEntryChange(entry.id, "operator", value)}
                  />
                </View>
                <View className="flex-1">
                  {property?.type === "boolean" ? (
                    <DropdownField
                      label="Value"
                      options={[
                        { value: "true", label: "True" },
                        { value: "false", label: "False" },
                      ]}
                      value={entry.value || "true"}
                      onChange={(value) => handleEntryChange(entry.id, "value", value)}
                    />
                  ) : (
                    <InputField
                      label="Value"
                      placeholder={(() => {
                        if (entry.operator === "$in" || entry.operator === "$nin") {
                          return "Comma separated values";
                        }
                        if (property?.type === "array" && entry.operator === "$eq") {
                          return "Comma separated values";
                        }
                        return "Enter value";
                      })()}
                      value={entry.value}
                      onChange={(value) => handleEntryChange(entry.id, "value", value)}
                      type={(() => {
                        if (property?.type === "number") {
                          return "number";
                        }
                        if (
                          property?.type === "array" &&
                          property.itemType === "number" &&
                          entry.operator !== "$eq" &&
                          entry.operator !== "$in" &&
                          entry.operator !== "$nin"
                        ) {
                          return "number";
                        }
                        return "text";
                      })()}
                    />
                  )}
                </View>
                <Button
                  variant="ghost"
                  onClick={() => handleRemoveEntry(entry.id)}
                  className="!py-2 md:mt-6 text-red-500 dark:text-red-300"
                >
                  <Icon
                    name="trash"
                    size={16}
                    className="mr-1"
                  />
                </Button>
              </View>
            </View>
          );
        })}

        {entries.length === 0 && (
          <View className="border border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-6 text-center">
            <Text className="text-sm text-gray-500 dark:text-gray-400">No success criteria defined. Add one to start.</Text>
          </View>
        )}

        <Button
          variant="ghost"
          onClick={handleAddEntry}
          className="self-start"
        >
          <Icon
            name="add"
            size={16}
            className="mr-1"
          />
          Add Criterion
        </Button>
      </View>
    </View>
  );
};
